import React, { useCallback, useEffect, useState } from "react";
import {
  Wrench,
  HelpCircle,
  Truck,
  Building2,
  Users,
  ExternalLink,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { motion } from "motion/react";
import type { SupportLinkDto } from "@mvk/shared";
import { useAppStore } from "../store/useAppStore";
import { supportLinksService } from "../services/supportLinksService";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { useTranslation } from "react-i18next";

const ICON_MAP: Record<string, LucideIcon> = {
  Wrench,
  HelpCircle,
  Truck,
  Building2,
  Users,
};

const ICON_COLORS: Record<string, { bg: string; text: string }> = {
  Wrench: { bg: "bg-orange-100", text: "text-orange-500" },
  HelpCircle: { bg: "bg-blue-100", text: "text-blue-500" },
  Truck: { bg: "bg-emerald-100", text: "text-emerald-500" },
  Building2: { bg: "bg-violet-100", text: "text-violet-500" },
  Users: { bg: "bg-indigo-100", text: "text-indigo-500" },
};

export function ChatsPage() {
  const { t } = useTranslation();
  const { selectedEventId } = useAppStore();
  const [links, setLinks] = useState<SupportLinkDto[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!selectedEventId) {
      setLinks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const items = await supportLinksService.listForEvent(selectedEventId);
      setLinks(items);
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        title={t("visitor.support_links_title")}
        subtitle={t("visitor.support_links_subtitle")}
      />
      <PageShell>
        {loading ? (
          <div className="flex justify-center py-12 text-gray-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : links.length === 0 ? (
          <div className="mt-8 text-center text-[13px] text-gray-400 px-6">
            {t("visitor.no_support_channels")}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {links.map((link, i) => {
              const IconComponent = ICON_MAP[link.icon] ?? HelpCircle;
              const colors = ICON_COLORS[link.icon] ?? ICON_COLORS.HelpCircle;
              return (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.3 }}
                >
                  <GlassCard
                    variant="default"
                    padding="sm"
                    className="cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => supportLinksService.openLink(link)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-11 h-11 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}
                      >
                        <IconComponent size={22} className={colors.text} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] text-gray-900 truncate">{link.title}</p>
                        <p className="text-[12px] text-gray-500 truncate mt-0.5">
                          {link.description}
                        </p>
                      </div>
                      <ExternalLink size={16} className="text-indigo-400 flex-shrink-0" />
                    </div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        )}
        <p className="mt-6 text-[11px] text-gray-400 text-center px-4 leading-relaxed">
          {t("visitor.support_links_hint")}
        </p>
      </PageShell>
    </>
  );
}
